/**
 * TCPN PHASE OTR-4 — Consent-Backed Before/After Trust Indicator
 * ===============================================================
 * A small, non-intrusive factual trust pill that surfaces when a provider's
 * public profile carries at least one Before/After case that has already
 * passed the backend's OTR-3 visibility predicate (consent_attestation ×
 * consent_source ∈ {written_consent, digital_consent} × consent_date ×
 * visibility=public × moderation=approved).
 *
 * Because the backend strips every non-eligible row server-side, the FE
 * only needs to ask: "did any row arrive?". This is a derived FE-only
 * boolean — NO new backend collection, NO new persistence, NO new
 * predicate, NO ranking impact, NO exposure of hidden counts.
 *
 * Safe-wording contract (locked):
 *   - factual + observable only
 *   - no legal / compliance claims (no "GDPR certified", "KVKK approved")
 *   - no count exposure of hidden/private/withdrawn items
 *   - silent empty state — renders `null` when no eligible items exist
 */
import React from 'react';
import { ShieldCheck } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * Pure predicate — returns true iff the public profile contains at least
 * one consent-backed Before/After item that has already been gated by the
 * backend `apply_visibility_policy`. Counts are NOT exposed externally.
 */
export const hasConsentBackedBeforeAfter = (profile) => {
  const showcase = profile?.treatment_showcase;
  if (!showcase || typeof showcase !== 'object') return false;
  for (const slug of Object.keys(showcase)) {
    const ba = showcase[slug]?.media?.before_after;
    if (Array.isArray(ba) && ba.length > 0) return true;
  }
  return false;
};

export const ConsentBackedIndicator = ({ profile, testId = 'consent-backed-indicator' }) => {
  if (!hasConsentBackedBeforeAfter(profile)) return null;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild data-testid={`${testId}-trigger`}>
          <span
            data-testid={testId}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200/70 bg-emerald-50/70 px-2.5 py-1 text-[11px] font-medium text-emerald-700"
          >
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Consent-backed before/after case available
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-xs bg-white border border-slate-200 text-slate-700 shadow-md p-3 rounded-md"
          data-testid={`${testId}-tooltip`}
        >
          <p className="text-xs font-semibold text-slate-900 mb-1">
            Before/after visibility
          </p>
          <ul className="space-y-1 text-[11px] leading-relaxed text-slate-600 list-none">
            <li data-testid={`${testId}-line-0`}>
              Before/after visibility requires explicit consent.
            </li>
            <li data-testid={`${testId}-line-1`}>
              Only approved public cases appear here.
            </li>
            <li data-testid={`${testId}-line-2`}>
              Consent can be withdrawn at any time, which removes the case
              from this page.
            </li>
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ConsentBackedIndicator;
